/**
 * Shared chart theme for the databases/dashboards system, matching Harbor
 * Ledger. The categorical palettes are dataviz-validated (six-checks
 * validator) against the card surfaces both palettes render on. Light mode
 * carries a contrast WARN on 4 hues, so charts must keep their legend/tooltip
 * labels (they do); dark mode's CVD floor is covered by the 2px surface gaps
 * between marks.
 *
 * Slots are assigned in the caller's stable series order and never re-derived
 * from rank, so a series keeps its color when data changes.
 *
 * THE COLOURS LIVE IN shared/theme/theme.css, not here. They are read off the
 * document root at call time (`--chart-series-1..8`, `--chart-other`,
 * `--chart-surface`, `--chart-grid`, `--chart-axis-text`), so charts follow a
 * theme switch — or any future brightness step — with no code change. The
 * constants below are only a fallback for when there is no DOM (jest, SSR) or
 * the stylesheet has not loaded yet; they must mirror theme.css.
 *
 * This replaces three separate hardcoded copies that had already drifted:
 * CostColumnChart, CostHistoryTimeline and StorageGrowthChart were all still
 * painting pre-Harbor greys (#e1e0d9 grid, #898781 axis text, #ffffff/#121212
 * surfaces) long after the theme moved.
 */

export const CHART_SERIES_LIGHT = [
  '#2a78d6', // blue
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#008300', // green
  '#4a3aa7', // violet
  '#e34948', // red
  '#e87ba4', // magenta
  '#eb6834', // orange
];

export const CHART_SERIES_DARK = [
  '#3987e5',
  '#199e70',
  '#c98500',
  '#008300',
  '#9085e9',
  '#e66767',
  '#d55181',
  '#d95926',
];

/** Fold color for series beyond the 8 slots — never a 9th hue. */
export const CHART_OTHER_COLOR = '#898781';

export interface ChartChrome {
  grid: string;
  axisText: string;
  surface: string;
}

/**
 * Fallbacks only. Live values come from theme.css via readToken(); these must
 * mirror the `--chart-*` / surface tokens defined there.
 */
export const CHART_CHROME: Record<'light' | 'dark', ChartChrome> = {
  light: { grid: '#bcc2ca', axisText: '#58636e', surface: '#eef2f8' },
  dark: { grid: '#2c343d', axisText: '#939ea9', surface: '#1c232b' },
};

/**
 * Read a custom property off the document root. Returns `fallback` when there
 * is no DOM or the property is undefined, so callers never paint `''`.
 */
function readToken(name: string, fallback: string): string {
  if (typeof document === 'undefined' || !document.documentElement) {
    return fallback;
  }
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
}

/**
 * Deliberately not memoised. Each call is one getComputedStyle plus a handful
 * of lookups, charts re-render rarely, and a cache would need invalidating on
 * every theme change — including ones a future brightness step introduces that
 * do not flip `data-theme`. Correct beats clever here.
 */
export function chartSeriesColors(theme: 'light' | 'dark'): string[] {
  const fallback = theme === 'dark' ? CHART_SERIES_DARK : CHART_SERIES_LIGHT;
  return fallback.map((hex, i) => readToken(`--chart-series-${i + 1}`, hex));
}

export function chartOtherColor(): string {
  return readToken('--chart-other', CHART_OTHER_COLOR);
}

export function chartChrome(theme: 'light' | 'dark'): ChartChrome {
  const fallback = CHART_CHROME[theme];
  return {
    grid: readToken('--chart-grid', fallback.grid),
    axisText: readToken('--chart-axis-text', fallback.axisText),
    surface: readToken('--chart-surface', fallback.surface),
  };
}
